import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Flatpickr from "react-flatpickr";
import { SERIES_FLATPICKR, tagSeriesCalendar } from "../seriesFlatpickr";

const toClock = (date) =>
  date ? `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}` : "";

const fromClock = (value) => {
  if (!value) return "";
  const [hours, minutes] = value.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

// Every prop handed to Flatpickr must keep its identity across renders: react-flatpickr
// pushes any prop that changed by reference back into the picker, which overwrites text
// that is typed but not yet committed (O-7964).
const TimeField = ({ value, onChange, className, ariaLabel, invalid, placeholder, disabled }) => {
  // Per instance, not module-level: react-flatpickr writes hook props into this object.
  // Being the same object every render also means react-flatpickr never sees an option
  // change, so nothing here may depend on props.
  const [options] = useState(() => ({
    ...SERIES_FLATPICKR,
    noCalendar: true,
    enableTime: true,
    dateFormat: "h:i K",
    minuteIncrement: 5,
    allowInput: true,
  }));
  const date = useMemo(() => fromClock(value), [value]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  const commit = useCallback((picked, picker) => {
    onChangeRef.current(toClock(picked), picker);
    // A parent can refuse a clear (or re-derive the same time), leaving `value` unchanged, so
    // react-flatpickr never calls setDate again. Put the saved time back rather than show a blank.
    if (!picked && valueRef.current) {
      setTimeout(() => {
        if (!picker.config) return; // destroyed: the field unmounted first
        if (valueRef.current && !picker.selectedDates.length) picker.setDate(fromClock(valueRef.current), false);
      });
    }
  }, []);
  const handleChange = useCallback(([picked], _text, picker) => commit(picked, picker), [commit]);
  // Clicking elsewhere parses the input without firing onChange (O-7965), so commit on close
  // when the parsed time differs from the saved one.
  const handleClose = useCallback(
    ([picked], _text, picker) => {
      if (toClock(picked) !== (valueRef.current || "")) commit(picked, picker);
    },
    [commit],
  );

  return (
    <Flatpickr
      className={["series-edit-input", "series-time-input", className, invalid ? "invalid" : ""]
        .filter(Boolean)
        .join(" ")}
      options={options}
      value={date}
      placeholder={placeholder}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      disabled={disabled}
      onChange={handleChange}
      onClose={handleClose}
      onReady={tagSeriesCalendar}
    />
  );
};

TimeField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  invalid: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default TimeField;
